import React, { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { useLoader } from '../../provider/LoaderProvider';
import useUsers from '../../context/UsersContext';
import taskListResourcePlannerService from '../../service/taskListResourcePlanner.service';
import EventChip from './EventChip';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { ButtonUI, CircularStaticProgressUI, SwitchUI } from '../Interface';
import GanttFilter from '../TaskListResourcePlanner/GanttFilter';
import useParam from '../../context/ParamContext';
import propertyService from '../../service/property.service';
import useMenus from '../../context/MenuContext';
import SimpleTaskListDialog from '../SimpleTaskList/SimpleTaskListDialog';
import useView from '../../hooks/useView';
import useSaveView from '../../hooks/useSaveView';
import {
  Button,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Popover,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import masterDataService from '../../service/masterData.service';
import taskListService from '../../service/taskList.service';

dayjs.extend(utc);
dayjs.extend(timezone);

const ALL_COLS = ['preparedness', 'startDate', 'endDate', 'description', 'contact'];

const SimpleTaskListPlannerPage = () => {
  const defaultCols = ALL_COLS.reduce((acc, k) => ((acc[k] = true), acc), {});
  const [colVisibility, setColVisibility] = useState(defaultCols);
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const filtersRef = useRef();
  const { getUser, user } = useUsers();
  const savedCols = useView('savedTaskListCols', user);
  const saveCols = useSaveView(user);
  const { params, getParam } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [tasks, setTasks] = useState({ resources: [], events: [], backgroundEvents: [] });
  const [dateRange, setDateRange] = useState({});
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [timeInterval, setTimeInterval] = useState({});
  const [intervalButtons, setIntervalButtons] = useState({});
  const [calendarViews, setCalendarViews] = useState({});
  const [selectedView, setSelectedView] = useState('');
  const [refetch, setRefetch] = useState(false);
  const [showEventReport, setShowEventReport] = useState(false);
  const [properties, setProperties] = useState([]);
  const [projectSubmenuId, setProjectSubmenuId] = useState(null);
  const [simpleTaskSubmenu, setSimpleTaskSubmenu] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState({});
  const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [initialState, setInitialState] = useState({
    filter: {
      filterModel: {
        items: [
          { field: 'active', operator: 'is', value: true },
          { field: 'construction', operator: 'is', value: true },
        ],
        logicOperator: 'and',
      },
    },
  });
  const [autocompleteData, setAutocompleteData] = useState({});
  const [task, setTask] = useState({});
  const [colsAnchorEl, setColsAnchorEl] = useState(null);
  const [headerPopover, setHeaderPopover] = useState({ anchor: null, colKey: null });
  const openColsPop = Boolean(colsAnchorEl);
  const openHeaderPop = Boolean(headerPopover.anchor);

  const slotIntervals = {
    day: { days: 1 },
    week: { weeks: 1 },
    month: { months: 1 },
  };

  const calendarDayViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'long' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'short' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'short' }, { day: 'numeric' }],
    },
  };

  const calendarWeekViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
  };

  const calendarMonthViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'long' }],
    },
  };

  useEffect(() => {
    setTimeInterval(slotIntervals.day);
    setCalendarViews(calendarDayViews);
    getParam();
    getMenus();
    if (!Object.keys(user).length) getUser();

    const toolbarElement = document.querySelector('.fc-toolbar-chunk');
    if (toolbarElement && filtersRef.current) {
      toolbarElement.appendChild(filtersRef.current);
    }
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('project');
    setProjectSubmenuId(submenuId);
    const simpleTaskSubmenuId = getMenuSubMenuId('simpleTask');
    setSimpleTaskSubmenu(simpleTaskSubmenuId);
  }, [menus]);

  useEffect(() => {
    if (Object.keys(savedCols).length > 0) {
      setColVisibility({ ...defaultCols, ...savedCols });
    }
  }, [savedCols]);

  useEffect(() => {
    if (dateRange.startDate) {
      getEvents();
    }
  }, [dateRange.startDate, initialState]);

  useEffect(() => {
    if (refetch) getEvents();
  }, [refetch]);

  useEffect(() => {
    getProjects();
  }, [initialState]);

  useEffect(() => {
    if (refetch) getProjects();
  }, [refetch]);

  useEffect(() => {
    if (simpleTaskSubmenu) initDefaultValues();
  }, [simpleTaskSubmenu]);

  useEffect(() => {
    if (Object.keys(params).length > 0) {
      const param = params.TASKLIST_RESOURCE_PLANNER_DYN_FILTERS
        ? JSON.parse(params.TASKLIST_RESOURCE_PLANNER_DYN_FILTERS)
        : [];

      if (param.length > 0) {
        getPropertyByIds(param);
      }
    }
  }, [params]);

  useEffect(() => {
    if (dateRange.startDate && showEventReport) {
      getReportEvents();
    } else {
      setTasks((prevState) => ({ ...prevState, backgroundEvents: [] }));
    }
  }, [dateRange.startDate, showEventReport, refetch]);

  useEffect(() => {
    setIntervalButtons(generateButtons(selectedView));
  }, [selectedView]);

  const getPropertyByIds = (ids) => {
    propertyService.getPropertyByIds({ id: ids }).then((data) => {
      setProperties(data);
    });
  };

  const getProjects = () => {
    showLoader();

    const filteredState = {
      ...initialState,
      filter: {
        ...initialState.filter,
        filterModel: {
          ...initialState.filter.filterModel,
          items: initialState.filter.filterModel.items.filter(
            (item) => item.field !== 'construction' && item.field !== 'startDate'
          ),
        },
      },
    };

    taskListResourcePlannerService
      .getV2Projects(filteredState)
      .then((data) => {
        graduallyLoadResources(data);
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const initDefaultValues = () => {
    masterDataService
      .statusToForm(simpleTaskSubmenu)
      .then((data) => {
        const aData = {
          status: data,
        };

        setAutocompleteData(aData);
      })
      .finally(() => {});
  };

  const persistCols = (next) => {
    setColVisibility(next);
    saveCols('savedTaskListCols', next);
  };

  const hideColumn = (key) => {
    const next = { ...colVisibility, [key]: false };
    persistCols(next);
  };

  const toggleColumn = (key) => {
    const next = { ...colVisibility, [key]: !colVisibility[key] };
    persistCols(next);
  };

  const openHeaderMenu = (e, colKey) => setHeaderPopover({ anchor: e.currentTarget, colKey });
  const closeHeaderMenu = () => setHeaderPopover({ anchor: null, colKey: null });

  const graduallyLoadResources = (all) => {
    const batchSize = 15;
    let index = batchSize;

    setTasks((prev) => {
      const firstBatch = all.slice(0, 1);
      return { ...prev, resources: firstBatch };
    });

    const expandNextBatch = () => {
      setTasks((prev) => {
        const nextBatch = all.slice(0, index + batchSize);
        return { ...prev, resources: nextBatch };
      });

      index += batchSize;
      if (index < all.length) {
        setTimeout(expandNextBatch, 25);
      }
    };

    setTimeout(expandNextBatch, 25);
  };

  const defaultStyles = {
    announced: {
      backgroundColor: '#8EE997',
      textColor: 'rgba(22, 163, 74, 1)',
    },
    nonAnnounced: {
      backgroundColor: 'rgba(254, 215, 170, 0.6)',
      textColor: 'rgba(249, 115, 22, 1)',
    },
  };

  const getEvents = () => {
    //showLoader();
    const startDateVal = dayjs(dateRange.startDate);

    const filteredState = {
      ...initialState,
      filter: {
        ...initialState.filter,
        filterModel: {
          ...initialState.filter.filterModel,
          items: [
            ...initialState.filter.filterModel.items.filter(
              (item) => !item.field.startsWith('dynamicProperty') && item.field !== 'projectName'
            ),
            { field: 'startDate', operator: 'onOrAfter', value: startDateVal },
          ],
        },
      },
    };

    taskListResourcePlannerService
      .getV2Events(filteredState)
      .then((data) => {
        const events = data.map((event) => {
          return {
            ...event,
            backgroundColor:
              event.preparedness < 100
                ? defaultStyles.nonAnnounced.backgroundColor
                : defaultStyles.announced.backgroundColor,
            textColor:
              event.preparedness < 100 ? defaultStyles.nonAnnounced.textColor : defaultStyles.announced.textColor,
          };
        });
        setTasks((prevState) => ({ ...prevState, events }));
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getReportEvents = () => {
    showLoader();
    taskListResourcePlannerService
      .getReportEvents(dateRange)
      .then((data) => {
        setTasks((prevState) => ({ ...prevState, backgroundEvents: data }));
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs.utc(dateInfo?.start).startOf('day');
    const endDate = dayjs.utc(dateInfo?.end).endOf('day');

    if (
      !dateRange.startDate ||
      !dateRange.endDate ||
      !startDate.isSame(dateRange.startDate) ||
      !endDate.isSame(dateRange.endDate)
    ) {
      setDateRange({ startDate, endDate });
    }

    if (selectedView !== dateInfo.view.type) setSelectedView(dateInfo.view.type);
  };

  const timeIntervalButtons = {
    day: {
      text: 'Nap',
      click: () => {
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);
      },
    },
    week: {
      text: 'Hét',
      click: () => {
        setTimeInterval(slotIntervals.week);
        setCalendarViews(calendarWeekViews);
      },
    },
    month: {
      text: 'Hónap',
      click: () => {
        setTimeInterval(slotIntervals.month);
        setCalendarViews(calendarMonthViews);
      },
    },
  };

  const generateButtons = (viewName) => {
    let retVal = {};
    switch (viewName) {
      case 'resourceTimelineDay':
        retVal = 'day';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineWeek':
        retVal = 'day,week';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineMonth':
        retVal = 'day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineYear':
        retVal = 'day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
    }
    return retVal;
  };

  const updateRelatedEventsColors = (event) => {
    const lineId = event.extendedProps.lineId;
    setTasks((prevState) => {
      const updatedEvents = prevState.events.map((e) => {
        if (e.lineId === lineId) {
          return { ...e, backgroundColor: 'rgba(254, 215, 170, 0.6)' };
        }
        return e;
      });
      return { ...prevState, events: updatedEvents };
    });
  };

  const restoreOriginalColors = () => {
    setTasks((prevState) => {
      const restoredEvents = prevState.events.map((event) => {
        return { ...event, ...defaultStyles };
      });
      return { ...prevState, events: restoredEvents };
    });
  };

  const onEventDragStart = (eventInfo) => {
    updateRelatedEventsColors(eventInfo.event);
  };

  const onEventResizeStart = (eventInfo) => {
    updateRelatedEventsColors(eventInfo.event);
  };

  const onEventDragStop = () => {
    restoreOriginalColors();
  };

  const onEventResizeStop = () => {
    restoreOriginalColors();
  };

  const updateEvent = (resizeInfo) => {
    const { event, revert } = resizeInfo;
    const resource = parseInt(event.getResources()[0].id);

    if (resource !== parseInt(event.extendedProps.eventData.lineId)) {
      addNotification({
        content: 'Az esemény nem helyezhető át',
        type: NotificationType.ERROR,
      });
      revert();
      return;
    }

    const duration = event.end ? dayjs(event.end).diff(dayjs(event.start), 'day') : null;
    const startDate = dayjs(event.start).toISOString();

    const updatedEventData = {
      startDate,
      duration,
    };

    showLoader();
    const id = event.id;

    taskListResourcePlannerService
      .updateEvent(updatedEventData, id)
      .then(() => {
        addNotification({
          content: 'Sikeres módosítás!',
          type: NotificationType.SUCCESS,
        });
        setRefetch(true);
      })
      .catch(() => {
        revert();
      })
      .finally(() => {
        hideLoader();
      });
  };

  const isSelectedResource = (resourceId) => {
    return resourceId === selectedRowId;
  };

  const resourceColumns = [
    {
      field: '',
      headerClassNames: 'col col0',
      headerContent: (arg) => (
        <Typography onClick={(e) => setColsAnchorEl(e.currentTarget)}>Oszlopok szerk.</Typography>
      ),
    },
    {
      field: ALL_COLS[0],
      headerText: 'Készültség',
      headerContent: (arg) => <Typography onClick={(e) => openHeaderMenu(e, ALL_COLS[0])}>Készültség</Typography>,
      headerClassNames: 'col preparednessCol',
      cellContent: function (arg) {
        const preparedness = arg.resource._resource.extendedProps.preparedness;

        return (
          <div className="absolute top-0.5 -translate-x-1/2 left-2/4">
            <CircularStaticProgressUI value={preparedness} circleColor="#3183FE" size={35} />
          </div>
        );
      },
      cellClassNames: (arg) => {
        const classNames = ['preparednessCol'];
        if (isSelectedResource(arg.resource.id)) {
          classNames.push('selected-row');
        } else if (arg.resource?.extendedProps?.type === 'PARENT' && !isSelectedResource(arg.resource.id)) {
          classNames.push('parent-row');
        }
        return classNames;
      },
    },
    {
      field: ALL_COLS[1],
      headerText: 'Kezdés',
      headerContent: (arg) => <Typography onClick={(e) => openHeaderMenu(e, ALL_COLS[1])}>Kezdés</Typography>,
      headerClassNames: 'col startDateCol',
      cellContent: function (arg) {
        const data = arg.resource._resource.extendedProps;
        return dayjs(data.startDate).format('YYYY.MM.DD');
      },
      cellClassNames: (arg) => {
        const classNames = ['startDateCol'];
        if (isSelectedResource(arg.resource.id)) {
          classNames.push('selected-row');
        } else if (arg.resource?.extendedProps?.type === 'PARENT' && !isSelectedResource(arg.resource.id)) {
          classNames.push('parent-row');
        }
        return classNames;
      },
    },
    {
      field: ALL_COLS[2],
      headerText: 'Befejezés',
      headerContent: (arg) => <Typography onClick={(e) => openHeaderMenu(e, ALL_COLS[2])}>Befejezés</Typography>,
      headerClassNames: 'col endDateCol',
      cellContent: function (arg) {
        const data = arg.resource._resource.extendedProps;
        return dayjs.utc(data?.endDate).format('YYYY.MM.DD');
      },
      cellClassNames: (arg) => {
        const classNames = ['endDateCol'];
        if (isSelectedResource(arg.resource.id)) {
          classNames.push('selected-row');
        } else if (arg.resource?.extendedProps?.type === 'PARENT' && !isSelectedResource(arg.resource.id)) {
          classNames.push('parent-row');
        }
        return classNames;
      },
    },
    {
      field: ALL_COLS[3],
      headerText: 'Megjegyzés',
      headerContent: (arg) => <Typography onClick={(e) => openHeaderMenu(e, ALL_COLS[2])}>Megjegyzés</Typography>,
      headerClassNames: 'col descCol',
      cellContent: function (arg) {
        const data = arg.resource._resource.extendedProps;
        return (
          <Tooltip
            arrow
            placement="top"
            title={<Typography>{data?.projectDescription}</Typography>}
            slotProps={{
              tooltip: {
                sx: {
                  color: '#514E6A',
                  backgroundColor: '#ffff',
                  boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                  minWidth: '200px',
                },
              },
              arrow: {
                sx: {
                  color: '#ffff',
                },
              },
            }}
          >
            <div className="truncate">{data?.projectDescription}</div>
          </Tooltip>
        );
      },
      cellClassNames: (arg) => {
        const classNames = ['endDateCol'];
        if (isSelectedResource(arg.resource.id)) {
          classNames.push('selected-row');
        } else if (arg.resource?.extendedProps?.type === 'PARENT' && !isSelectedResource(arg.resource.id)) {
          classNames.push('parent-row');
        }
        return classNames;
      },
    },
    {
      field: ALL_COLS[4],
      headerText: 'Kapcsolattartó',
      headerContent: (arg) => <Typography onClick={(e) => openHeaderMenu(e, ALL_COLS[2])}>Kapcsolattartó</Typography>,
      headerClassNames: 'col contactCol',
      cellContent: function (arg) {
        const data = arg.resource._resource.extendedProps;
        return data?.projectContactName;
      },
      cellClassNames: (arg) => {
        const classNames = ['endDateCol'];
        if (isSelectedResource(arg.resource.id)) {
          classNames.push('selected-row');
        } else if (arg.resource?.extendedProps?.type === 'PARENT' && !isSelectedResource(arg.resource.id)) {
          classNames.push('parent-row');
        }
        return classNames;
      },
    },
  ];

  const visibleResourceColumns = resourceColumns.filter(
    (col) => col.field === '' || colVisibility[col.field] !== false
  );

  const getColLabel = (key) => {
    const col = resourceColumns.find((c) => c.field === key);
    return col?.headerText;
  };

  const handleSelect = (selectInfo) => {
    const { resource, start, end } = selectInfo;
    const startDate = dayjs(start).utc().startOf('day');
    const endDate = dayjs(end).utc().startOf('day');
    const duration = endDate.diff(start, 'day');

    setSelectedRowData({
      startDate,
      duration,
      lineId: parseInt(resource.id),
      formId: projectSubmenuId,
      name: 'Feladat',
      statusId: autocompleteData?.status?.defaultValue ?? null,
      taskListRespPerson: [],
      createdBy: user.userId,
    });

    setSelectedRowId(resource.id);
  };

  const createTaskList = () => {
    if (!selectedRowData.statusId) {
      addNotification({
        content: 'Nincs alapértelmezett státusz beállítva a feladatokhoz!',
        type: NotificationType.ERROR,
      });
      return;
    }
    const formData = new FormData();
    formData.append('data', JSON.stringify(selectedRowData));

    taskListService.createTaskList(formData).then((data) => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      setSelectedRowData({});
      setSelectedRowId(null);
      setRefetch(true);
    });
  };

  const getResourceLabelClassNames = (resource) => {
    let classes = [];
    if (resource.id === selectedRowId) {
      classes.push('selected-row');
    }

    if (resource?.extendedProps?.type === 'PARENT' && resource.id !== selectedRowId) {
      classes.push('parent-row');
    }

    if (!resource._resource.parentId) {
      classes.push('fc-res-group');
    }
    return classes;
  };

  const getTaskById = (id) => {
    showLoader();
    taskListService
      .getTasklistById(id)
      .then((data) => {
        setTask(data[0]);
        setTaskDialogOpen(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  return (
    <div className="taskListCalendar simpleTaskListCalendar">
      <div ref={filtersRef}>
        <GanttFilter
          showEventReportState={{ showEventReport, setShowEventReport }}
          properties={properties}
          initial={{ initialState, setInitialState }}
          setNewTaskDialogOpen={setNewTaskDialogOpen}
          selectedRowState={{ selectedRowData, setSelectedRowData, setSelectedRowId }}
          showAddTask={true}
          createTaskList={createTaskList}
        />
      </div>
      <FullCalendar
        customButtons={timeIntervalButtons}
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        initialView="resourceTimelineMonth"
        resources={tasks.resources}
        locales={[huLocale]}
        locale="hu"
        select={handleSelect}
        slotLabelFormat={[
          { month: 'long', year: 'numeric', weekday: 'long' },
          { hour: 'numeric', minute: '2-digit' },
        ]}
        resourceOrder="visOrder"
        datesSet={handleDatesSet}
        events={[...tasks.events, ...tasks.backgroundEvents]}
        editable={true}
        nowIndicator={true}
        selectable={true}
        eventResizeStart={onEventResizeStart}
        eventDragStart={onEventDragStart}
        eventDragStop={onEventDragStop}
        eventResizeStop={onEventResizeStop}
        eventResize={updateEvent}
        eventDrop={updateEvent}
        eventContent={(eventInfo) => {
          if (eventInfo.event.display !== 'background') {
            return <EventChip eventInfo={eventInfo} defaultStyles={defaultStyles} allEvents={tasks.events} />;
          }
        }}
        headerToolbar={{
          left: `prev,next,today ${intervalButtons}`,
          center: 'title',
          right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,resourceTimelineYear',
        }}
        eventClick={(clickInfo) => {
          if (clickInfo.event.display !== 'background') {
            const id = clickInfo.event.id;
            getTaskById(id);
          }
        }}
        views={calendarViews}
        slotDuration={timeInterval}
        resourceAreaWidth="25%"
        resourceAreaHeaderContent="Erőforrás"
        scrollTimeReset={false}
        resourceAreaColumns={visibleResourceColumns}
        timeZone="UTC"
        resourcesInitiallyExpanded={false}
        resourceLabelClassNames={(data) => getResourceLabelClassNames(data.resource)}
      />

      <SimpleTaskListDialog
        open={newTaskDialogOpen}
        setTaskChanged={setRefetch}
        handleClose={() => {
          setNewTaskDialogOpen(false);
        }}
      />

      <SimpleTaskListDialog
        open={taskDialogOpen}
        setTaskChanged={setRefetch}
        task={task}
        handleClose={() => {
          setTaskDialogOpen(false);
        }}
      />

      <Popover
        open={openColsPop}
        anchorEl={colsAnchorEl}
        onClose={() => setColsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <List dense sx={{ minWidth: 240 }}>
          {ALL_COLS.map((key) => (
            <ListItem key={key} sx={{ py: 0 }}>
              <ListItemText primary={getColLabel(key)} />
              <FormControlLabel
                control={<SwitchUI checked={!!colVisibility[key]} onChange={() => toggleColumn(key)} />}
                label=""
                sx={{ m: 0 }}
              />
            </ListItem>
          ))}
        </List>
      </Popover>

      <Popover
        open={openHeaderPop}
        anchorEl={headerPopover.anchor}
        onClose={closeHeaderMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <List dense sx={{ p: 0 }}>
          <ListItem
            button
            onClick={() => {
              if (headerPopover.colKey) hideColumn(headerPopover.colKey);
              closeHeaderMenu();
            }}
          >
            <ListItemText primary="Elrejtés" />
          </ListItem>
        </List>
      </Popover>
    </div>
  );
};

export default SimpleTaskListPlannerPage;
